import { Component, ElementRef, OnDestroy, OnInit, Renderer2, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SelectItem } from 'primeng/api';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { SignalRService } from 'src/app/shared/services/signal-r.service';
import { isEqual } from 'lodash-es';
import { SessionStorageKeys, StoreService } from '../../services/store.service';
import { Subject } from 'rxjs';
import { SelectChangeEvent } from 'primeng/select';
import { CurrentAppIdsDto } from '../../models/current-app-ids-dto';

interface PeriforMsg {
  id: string;
  data: any;
  detail: any;
  severity: string;
  summary: string;
}

@Component({
  selector: 'app-notification-hub',
  templateUrl: './notification-hub.component.html',
  styles: [`
    .hub-container {
      padding: 1em;
      height: 375px;
      width: 500px;
      overflow: auto;
    }
  `],
  standalone: false
})
export class NotificationHubComponent implements OnInit, OnDestroy {

  private _destroy$ = new Subject<void>();
  private _messagesBackup = signal<PeriforMsg[]>([]);
  public messages = signal<PeriforMsg[]>([]);
  public selectedSeverity = signal<any>('all');
  public showWindow = signal<boolean>(false);

  // Then, a derived signal for designId
  readonly designId = computed(() => this.appIds()?.designId ?? null);

  private removeClickListener?: () => void;

  public messageTypes: SelectItem[] = [
    { label: 'All', value: 'all' },
    { label: 'Success', value: 'success' },
    { label: 'Info', value: 'info' },
    { label: 'Error', value: 'error' },
    { label: 'Warning', value: 'warn' }
  ];

  public messagesUnread = computed(() => {
    //Don't show unread, if they've left the notification window open.
    if (this.showWindow()) {
      return 0;
    }
    return this.messages().filter(m => m.data?.read === false).length;
  });

  public filteredMessages = computed(() => {
    const severity = this.selectedSeverity() ?? 'all';
    const filteredMessages = severity === 'all' ? this.messages() : this.messages().filter(msg => msg.severity === severity);

    // Sort messages by time to preserve order,`msg.data.time` is a date string
    return filteredMessages.sort((a, b) => {
      const timeA = new Date(a.data?.time ?? 0).getTime();
      const timeB = new Date(b.data.time ?? 0).getTime();
      return timeB - timeA; // Most recent first (descending)
    });
  });

  public appMessageNoticeService = inject(AppNotificationService); // Use inject
  private _signalRService = inject(SignalRService);
  private _storeService = inject(StoreService);
  private _initialLoadDone = signal<boolean>(false);
  private renderer: Renderer2 = inject(Renderer2);
  private el: ElementRef = inject(ElementRef);

  private appIds = toSignal(
    this._storeService.watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS)
  );

  constructor() {
    // Effect to initialize messages after the service has loaded them
    effect(() => {
      const initialMessages = this.appMessageNoticeService.msgs;
      if (initialMessages().length > 0 && !this._initialLoadDone()) {
        const processedMessages = initialMessages()
          .map(m => this.processMessage(m))
          .reverse(); // newest first
        this.messages.set(processedMessages);
        this._messagesBackup.set(processedMessages);
        this._initialLoadDone.set(true);
      }
    });

    // Effect to react to new messages from the service
    effect(() => {
      const newMessage = this.appMessageNoticeService.latestMessage();
      if (newMessage) {
        const processedMessage = this.processMessage(newMessage);
        this.messages.update(currentMessages => [processedMessage, ...currentMessages]);
        this._messagesBackup.update(currentBackup => [processedMessage, ...currentBackup]);
      }
    });
  }

  ngOnInit(): void {
    this.setupSignalR();
    this.setupClickOutsideListener();
  }

  public openWindow() {
    if (this.showWindow()) {
      this.closeWindow();
      return;
    }
    this.markRead();
    this.showWindow.set(true);
  }

  public openWindowBadgeClick($event) {
    $event?.stopPropagation();
    this.openWindow();
  }

  public closeWindow() {
    this.showWindow.set(false);
  }

  private processMessage(m: any): PeriforMsg {
    const severity = m.severity || 'info';
    let summary = m.summary;
    let detail = m.detail;

    //TODO: This shouldn't be in here.
    if (!detail && summary?.includes('Thermal operation is linked')) {
      detail = summary.slice(summary.indexOf('Thermal operation is linked'));
      summary = 'PERICAL';
    }

    if (!detail) {
      detail = summary;
      summary = this.capitalizeFirstLetter(severity);
    }

    return {
      id: m.id,
      severity,
      summary,
      detail,
      data: m.data
    };
  }

  private setupSignalR(): void {
    const signalRHub = this._signalRService.getConnectionToApplicationInfoMessageHub();
    signalRHub.on(SignalRService.ON_APP_INFO, (data: any) => {
      if (this.designId() === data.designId) {
        const msg = { severity: data.level, summary: data.summary, detail: data.message, data: `${data.designId}` };
        this.appMessageNoticeService.showMessage(msg);
      }
    });
  }

  onSeverityChange(event: SelectChangeEvent): void {
    this.selectedSeverity.set(event.value);
  }

  clearNotifications(): void {
    this.appMessageNoticeService.clearMessages();
    this.messages.set([]);
    this._messagesBackup.set([]);
  }

  removeNotification(msg: PeriforMsg, event: any): void {
    // Prevent the click from propagating to the document
    // Only call stopPropagation if event exists
    event?.originalEvent?.stopImmediatePropagation();

    this.appMessageNoticeService.removeMessage(msg);
    this.messages.update(currentMessages => currentMessages.filter(f => !isEqual(f, msg)));
    this._messagesBackup.update(currentBackup => currentBackup.filter(f => !isEqual(f, msg)));
    if (!this.messages().some(x => x.severity === this.selectedSeverity()?.value)) {
      this.selectedSeverity.set('all');
    }
  }

  copyNotification(message: PeriforMsg): void {
    navigator.clipboard.writeText(message.detail || message.summary);
  }

  markRead(): void {
    this.messages.update(currentMessages =>
      currentMessages.map(m => ({ ...m, data: { ...m.data, read: true } }))
    );
    this._messagesBackup.update(currentBackup =>
      currentBackup.map(m => ({ ...m, data: { ...m.data, read: true } }))
    );
    this.appMessageNoticeService.markRead();
    this.selectedSeverity.set('all');
  }

  private capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  setupClickOutsideListener(): void {
    this.removeClickListener = this.renderer.listen('document', 'click', this.handleClickOutside);
  }

  removeClickOutsideListener(): void {
    if (this.removeClickListener) {
      this.removeClickListener();
      this.removeClickListener = undefined;
    }
  }

  handleClickOutside = (event: MouseEvent): void => {
    const clickedInsideComponent = this.el.nativeElement.contains(event.target);
    if (!clickedInsideComponent) {
      this.showWindow.set(false);  // Close the notification card
    }
  };

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this.removeClickOutsideListener();
  }

}